import React from "react";
import { Anchor, CheckCircle2, Compass, Fuel, MapPin, ShieldCheck } from "lucide-react";

const checklist = [
  "Confirm guest berth availability before arrival, especially in peak season.",
  "Check approach depth, shelter from swell, and local harbour entrance notes.",
  "Review fuel, water, electricity, repair support, and waste disposal options.",
  "Compare the latest marine forecast with your vessel size and crew experience.",
];

const regions = [
  {
    title: "Eastern Mediterranean",
    text: "Plan around afternoon sea breezes, summer marina demand, and limited overnight berths in smaller fishing shelters.",
  },
  {
    title: "Greek islands",
    text: "Check shelter from meltemi winds, ferry wash, mooring style, and whether fuel is delivered by truck or available dockside.",
  },
  {
    title: "Cyprus coast",
    text: "Review harbour entrance exposure, customs needs, fuel access, and nearby safe alternatives before committing to arrival.",
  },
];

const features = [
  { icon: MapPin, label: "Location context", text: "Compare marinas by cruising area, approach conditions, and practical arrival notes." },
  { icon: Fuel, label: "Service planning", text: "Think through fuel, water, repairs, shore power, provisioning, and crew turnaround needs." },
  { icon: ShieldCheck, label: "Safety first", text: "Use marina information together with official notices, charts, weather, and local harbour guidance." },
];

export default function MarinasApprovalGuide() {
  return (
    <div className="space-y-8">
      <section className="relative overflow-hidden rounded-[2rem] border border-border bg-card p-6 shadow-xl md:p-10">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-atlantic to-primary" />
        <p className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-primary">
          <Anchor className="h-4 w-4" /> Marina planning guide
        </p>
        <div className="mt-5 grid gap-8 lg:grid-cols-[1.12fr_.88fr] lg:items-center">
          <div>
            <h1 className="max-w-4xl font-heading text-[clamp(2rem,5vw,4rem)] font-black leading-[1.02] tracking-[-0.045em] text-foreground">
              Find safer marina stops with practical arrival checks
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-8 text-muted-foreground md:text-lg">
              Marstick helps sailors prepare marina arrivals by combining location context, marine-weather awareness, berth-planning questions, and safety reminders in one clear workflow. Use this guide before contacting a harbour, booking a berth, or choosing a fallback stop.
            </p>
          </div>
          <div className="rounded-[1.5rem] border border-border bg-background/70 p-5">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary text-primary-foreground">
                <Compass className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-black text-foreground">Before arrival</p>
                <p className="text-sm leading-6 text-muted-foreground">Verify berth, weather, depth, and services.</p>
              </div>
            </div>
            <ul className="mt-5 space-y-3">
              {checklist.map((item) => (
                <li key={item} className="flex gap-3 text-sm leading-6 text-foreground">
                  <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {features.map(({ icon: Icon, label, text }) => (
          <article key={label} className="rounded-[1.5rem] border border-border bg-card p-5 shadow-lg">
            <Icon className="h-6 w-6 text-primary" />
            <h2 className="mt-4 text-lg font-black text-foreground">{label}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
          </article>
        ))}
      </section>

      <section className="rounded-[1.5rem] border border-border bg-card p-6 shadow-lg md:p-8">
        <p className="text-xs font-black uppercase tracking-[0.2em] text-primary">Regional examples</p>
        <h2 className="mt-3 text-2xl font-black tracking-tight text-foreground">How to compare marina stops</h2>
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          {regions.map((region) => (
            <article key={region.title} className="rounded-2xl border border-border bg-background/65 p-4">
              <h3 className="text-base font-black text-foreground">{region.title}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{region.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="rounded-[1.5rem] border border-primary/25 bg-primary/10 p-5">
        <h2 className="text-lg font-black text-foreground">Important safety note</h2>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          Marina and weather information should support, not replace, official nautical charts, harbour authorities, notices to mariners, and the skipper’s judgement. Always confirm current conditions directly with the marina or local authority before arrival.
        </p>
      </section>
    </div>
  );
}